// src/Features/Projects/components/ProjectEditForm/ProjectEditForm.tsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import {
  PencilIcon,
  SparklesIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@/contexts/AuthContext";
import { FormInput, FormTextarea, FormButton } from "@/components";
import {
  useProjectMutations,
  useGeminiMutations,
  editProjectSchema,
  geminiService,
} from "@/features/Projects";
import type { EditProjectFormData } from "@/features/Projects";
import type { ProjectEditFormProps } from "./projectEditForm.types";
import { FORM_PLACEHOLDERS } from "./projectEditForm.definitions";

export default function ProjectEditForm({
  project,
  onSuccess,
  onCancel,
}: ProjectEditFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { update, isUpdating } = useProjectMutations();
  const { expandDescription, isExpandingDescription } = useGeminiMutations();

  const [tempDescription, setTempDescription] = useState("");
  const [isExpanding, setIsExpanding] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
    getValues,
  } = useForm<EditProjectFormData>({
    resolver: yupResolver(editProjectSchema),
    mode: "onChange",
    defaultValues: {
      name: project.name,
      description: project.description,
    },
  });

  const watchedDescription = watch("description");

  // Atualizar projeto
  const handleUpdateProject = async (data: EditProjectFormData) => {
    try {
      await update({ id: project.id, data });

      onSuccess?.(project.id);
      navigate(`/projects/${project.id}`);
    } catch (error) {
      console.error("Erro ao atualizar projeto:", error);
    }
  };

  // Expandir/Re-expandir descrição com Gemini
  const handleExpandDescription = async () => {
    const currentDescription = getValues("description");

    if (!currentDescription || currentDescription.length < 10) {
      return;
    }

    setIsExpanding(true);

    try {
      // Usa a descrição atual como "inicial" para re-expandir
      const response = await expandDescription({
        initialDescription: currentDescription,
      });

      // Atualiza o campo description com a nova versão expandida
      setValue("description", response.expandedDescription, {
        shouldValidate: true,
        shouldDirty: true,
      });

    } catch (error) {
      console.error("Erro ao expandir descrição:", error);
    } finally {
      setIsExpanding(false);
    }
  };

  // Cancelar operação
  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        "Você tem alterações não salvas. Deseja realmente cancelar?"
      );
      if (!confirmed) return;
    }

    onCancel?.();
    navigate(`/projects/${project.id}`);
  };

  const isLoading = isUpdating || isExpanding;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-raro border border-primary-100">
        {/* Header */}
        <div className="px-8 py-6 border-b border-primary-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl">
                <PencilIcon className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-raro-gray-900 font-montserrat">
                  Editar Projeto
                </h1>
                <p className="text-sm text-raro-gray-600">
                  Atualize as informações e use a IA para expandir se necessário
                </p>
              </div>
            </div>

            {geminiService.isConfigured() && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-primary-50 rounded-lg">
                <SparklesIcon className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-medium text-primary-700">
                  IA Ativada
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleUpdateProject)}
          className="p-8 space-y-6"
        >
          {/* Nome do projeto */}
          <div>
            <FormInput
              label="Nome do Projeto"
              placeholder={FORM_PLACEHOLDERS.name}
              error={errors.name?.message}
              required
              disabled={isLoading}
              {...register("name")}
            />
          </div>

          {/* Descrição do projeto */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-raro-gray-700">
                Descrição do Projeto <span className="text-red-500">*</span>
              </label>
              
              {geminiService.isConfigured() && watchedDescription.length >= 10 && (
                <FormButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleExpandDescription}
                  loading={isExpanding}
                  disabled={isLoading}
                  icon={<SparklesIcon className="w-4 h-4" />}
                  iconPosition="left"
                >
                  Re-expandir com IA
                </FormButton>
              )}
            </div>

            <FormTextarea
              placeholder={FORM_PLACEHOLDERS.expandedDescription}
              error={errors.description?.message}
              required
              disabled={isLoading}
              rows={12}
              showCharCount
              maxLength={5000}
              {...register("description")}
            />

            <div className="mt-2 text-xs text-raro-gray-500">
              💡 Você pode editar livremente ou usar a IA para re-expandir o conteúdo atual.
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-4 pt-6 border-t border-primary-100">
            <FormButton
              type="button"
              variant="secondary"
              size="lg"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </FormButton>

            <FormButton
              type="submit"
              variant="primary"
              size="lg"
              loading={isUpdating}
              disabled={isLoading || !isValid || !isDirty}
              icon={<DocumentTextIcon className="w-5 h-5" />}
              iconPosition="left"
              fullWidth
            >
              Salvar Alterações
            </FormButton>
          </div>

          {/* Dica sobre IA */}
          {!geminiService.isConfigured() && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <SparklesIcon className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 font-montserrat">
                    IA não configurada
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Configure a API do Gemini para usar re-expansão automática da
                    descrição.
                  </p>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}